import { LocalFavoritesTypes, ProfessionalFavoritesTypes } from '@/types';
import { restApiClient } from '@/datasources';

interface PaginatedParams {
  page?: number;
  limit?: number;
}

interface ProfessionalsParams extends PaginatedParams {
  withSpecialties?: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  count: number;
}

export async function getProfessional(
  params: ProfessionalsParams = {},
): Promise<PaginatedResponse<ProfessionalFavoritesTypes>> {
  const { page, limit, withSpecialties = true } = params;
  const { data: professionalsResponse } = await restApiClient.get<PaginatedResponse<ProfessionalFavoritesTypes>>(
    'online/customers/favorites/professionals',
    {
      page,
      limit,
      withSpecialties,
    },
  );

  return professionalsResponse;
}

export async function getLocals(params: PaginatedParams = {}): Promise<PaginatedResponse<LocalFavoritesTypes>> {
  const { page, limit } = params;
  const { data: localsResponse } = await restApiClient.get<PaginatedResponse<LocalFavoritesTypes>>(
    'online/customers/favorites/locations',
    {
      page,
      limit,
    },
  );

  return localsResponse;
}

export async function addProfessionalFavorite(professionalId: string): Promise<void> {
  await restApiClient.post(`online/professionals/${professionalId}/favorite`, {});
}

export async function removeProfessionalFavorite(professionalId: string): Promise<void> {
  await restApiClient.post(`online/professionals/${professionalId}/unfavorite`, {});
}

export async function checkIfProfessionalIsFavorite(professionalId: string): Promise<boolean> {
  const { data: favoritedProfessionals } = await getProfessional({ withSpecialties: false });
  return favoritedProfessionals.some((item) => item.professional.id === professionalId);
}

export async function addPartnerFavorite(partnerId: string): Promise<void> {
  await restApiClient.post(`online/partner/clinic/${partnerId}/favorite`, {});
}

export async function removePartnerFavorite(partnerId: string): Promise<void> {
  await restApiClient.post(`online/partner/clinic/${partnerId}/unfavorite`, {});
}

export async function checkIfPartnerIsFavorite(partnerId: string): Promise<boolean> {
  const { data: favoritedPartners } = await getLocals();
  return favoritedPartners.some((item) => item?.partner?.id === partnerId);
}

export async function addBranchLaboratoryFavorite(branchLaboratoryId: string): Promise<void> {
  await restApiClient.post(`online/branch-laboratory/${branchLaboratoryId}/favorite`, {});
}

export async function removeBranchLaboratoryFavorite(branchLaboratoryId: string): Promise<void> {
  await restApiClient.post(`online/branch-laboratory/${branchLaboratoryId}/unfavorite`, {});
}

export async function checkIfBranchLaboratoryIsFavorite(branchLaboratoryId: string): Promise<boolean> {
  const { data: favoritedPartners } = await getLocals();

  return favoritedPartners.some((item) => item.branchLaboratory?.id === branchLaboratoryId);
}

export async function addBranchFavorite(branchLaboratoryId: string): Promise<void> {
  await restApiClient.post(`online/branches/${branchLaboratoryId}/favorite`, {});
}

export async function removeBranchFavorite(branchLaboratoryId: string): Promise<void> {
  await restApiClient.post(`online/branches/${branchLaboratoryId}/unfavorite`, {});
}

export async function checkIfBranchIsFavorite(branchId: string): Promise<boolean> {
  const { data: favoritedLocals } = await getLocals();

  return favoritedLocals.some((item) => item.branch?.id === branchId);
}
